import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthPageShell } from '@/features/auth/components/auth-page-shell'
import { normalizeReturnUrl } from '@/features/auth/guards'
import { loginWithPassword } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

type LoginSearch = {
  redirect?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginRoute,
})

function LoginRoute() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const setSession = useAuthSessionStore((state) => state.setSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const target = normalizeReturnUrl(redirect)

  if (isAuthenticated()) {
    void navigate({ to: target })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email.trim() || !password) {
      toast.error('请输入邮箱和密码')
      return
    }

    setIsSubmitting(true)

    try {
      const session = await loginWithPassword({
        email: email.trim(),
        password,
      })
      setSession(session)
      setInitialized(true)
      if (!session.emailVerified) {
        toast.message('请先完成邮箱验证')
        await navigate({ to: '/verify-email', search: { redirect: target } })
        return
      }
      toast.success('登录成功')
      await navigate({ to: target })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '登录失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell
      title="登录 JIAN"
      description="登录后即可使用 AI 配音、生图、视频生成等全部工具。"
      footerText="还没有账号？"
      footerActionLabel="立即注册"
      footerActionTo="/register"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">邮箱</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="auth-input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">密码</Label>
          <div className="relative"><Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="请输入密码"
            className="auth-input"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-[#697184]" aria-label={showPassword ? '隐藏密码' : '显示密码'}>{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</button></div>
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
          {isSubmitting ? '登录中...' : '登录并进入工作台'}
        </Button>
      </form>
    </AuthPageShell>
  )
}
